import { useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import { useProductMutations } from './api';
import { useToast } from '../components/Toast';
import { ApiRequestError } from '../lib/api';
import { PendingPhotoPicker } from './ProductPhotos';

const BLANK = {
  barcodeNo: '',
  photoId: '',
  size: '',
  grossWeight: '',
  stoneWeight: '',
  stoneAmount: '',
  displayName: '',
  category: '',
  subcategory: '',
  displayPrice: '',
  sellingPrice: '',
  baseQuantity: '0',
  tags: '',
  status: 'active',
  description: '',
};

export function ProductCreateModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { create, addImages } = useProductMutations();
  const toast = useToast();
  const [f, setF] = useState({ ...BLANK });
  const [photos, setPhotos] = useState<File[]>([]);
  const set = (k: keyof typeof BLANK, v: string) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) {
      setF({ ...BLANK });
      setPhotos([]);
    }
  }, [open]);

  const busy = create.isPending || addImages.isPending;

  const save = async () => {
    if (!f.barcodeNo.trim()) return toast('Barcode number is required', 'error');
    if (!/^\d+(\.\d+)?$/.test(f.grossWeight.trim()) || Number(f.grossWeight) <= 0)
      return toast('Gross weight must be a positive number', 'error');

    const body: Record<string, unknown> = {
      barcodeNo: f.barcodeNo.trim(),
      grossWeight: f.grossWeight.trim(),
      photoId: f.photoId.trim() || null,
      size: f.size.trim() || null,
      stoneWeight: f.stoneWeight.trim() || null,
      stoneAmount: f.stoneAmount.trim() || null,
      displayName: f.displayName.trim() || undefined,
      description: f.description.trim() || undefined,
      category: f.category.trim() || undefined,
      subcategory: f.subcategory.trim() || undefined,
      displayPrice: f.displayPrice.trim() || null,
      sellingPrice: f.sellingPrice.trim() || null,
      baseQuantity: Number(f.baseQuantity) || 0,
      tags: f.tags ? f.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      status: f.status,
    };

    try {
      const product = await create.mutateAsync(body);
      if (photos.length) {
        try {
          await addImages.mutateAsync({ id: product._id, files: photos });
        } catch (e) {
          toast(
            `Product created, but photo upload failed: ${
              e instanceof ApiRequestError ? e.message : 'unknown error'
            }`,
            'error',
          );
          onSaved?.();
          onClose();
          return;
        }
      }
      toast(`Added ${product.barcodeNo}`, 'success');
      onSaved?.();
      onClose();
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Could not add jewellery', 'error');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add jewellery"
      size="lg"
      footer={
        <>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Add item'}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <L label="Barcode number *">
          <input
            className="input font-mono"
            value={f.barcodeNo}
            onChange={(e) => set('barcodeNo', e.target.value)}
            placeholder="e.g. 15441915"
          />
        </L>
        <L label="Photo ID">
          <input className="input" value={f.photoId} onChange={(e) => set('photoId', e.target.value)} />
        </L>
        <L label="Size">
          <input className="input" value={f.size} onChange={(e) => set('size', e.target.value)} />
        </L>
        <L label="Gross weight (g) *">
          <input
            className="input"
            inputMode="decimal"
            value={f.grossWeight}
            onChange={(e) => set('grossWeight', e.target.value)}
          />
        </L>
        <L label="Stone weight (g)">
          <input
            className="input"
            inputMode="decimal"
            value={f.stoneWeight}
            onChange={(e) => set('stoneWeight', e.target.value)}
          />
        </L>
        <L label="Stone amount (₹)">
          <input
            className="input"
            inputMode="decimal"
            value={f.stoneAmount}
            onChange={(e) => set('stoneAmount', e.target.value)}
          />
        </L>
        <L label="Display name" span>
          <input
            className="input"
            value={f.displayName}
            onChange={(e) => set('displayName', e.target.value)}
          />
        </L>
        <L label="Category">
          <input className="input" value={f.category} onChange={(e) => set('category', e.target.value)} />
        </L>
        <L label="Subcategory">
          <input
            className="input"
            value={f.subcategory}
            onChange={(e) => set('subcategory', e.target.value)}
          />
        </L>
        <L label="Display price (₹)">
          <input
            className="input"
            inputMode="decimal"
            value={f.displayPrice}
            onChange={(e) => set('displayPrice', e.target.value)}
          />
        </L>
        <L label="Selling price (₹)">
          <input
            className="input"
            inputMode="decimal"
            value={f.sellingPrice}
            onChange={(e) => set('sellingPrice', e.target.value)}
          />
        </L>
        <L label="Base quantity">
          <input
            className="input"
            inputMode="numeric"
            value={f.baseQuantity}
            onChange={(e) => set('baseQuantity', e.target.value)}
          />
        </L>
        <L label="Status">
          <select className="input" value={f.status} onChange={(e) => set('status', e.target.value)}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="archived">archived</option>
          </select>
        </L>
        <L label="Tags (comma separated)" span>
          <input className="input" value={f.tags} onChange={(e) => set('tags', e.target.value)} />
        </L>
        <L label="Description" span>
          <textarea
            className="input min-h-[64px]"
            value={f.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </L>
      </div>

      <div className="mt-4 border-t border-ink-100 pt-4">
        <PendingPhotoPicker files={photos} onChange={setPhotos} />
      </div>
    </Modal>
  );
}

function L({ label, span, children }: { label: string; span?: boolean; children: React.ReactNode }) {
  return (
    <label className={span ? 'col-span-2 block' : 'block'}>
      <span className="label">{label}</span>
      {children}
    </label>
  );
}
